import React, { useMemo, useEffect, useRef } from 'react';
import moment from 'moment';
import Conditions from './Conditions';
import EmptyTable from '../EmptyTable';
import { getLabel } from '../../utils/i18n';
import { fetchLendingDataSource } from '../../actions/actionsSet/lending';
import usePagination from '../../hooks/usePagination';
import { pencentage } from '../../utils/number';

const TIME_FORMAT = 'YYYY-MM-DD';
const TIME_HH_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const RowMap = {
  'Assets': ['Product-Name', 'Coin', 'Holding', 'Amount', 'Duration', 'Value Date', 'Redemption Date', 'Coupon', 'APR'],
  'Subscription': ['Subscription Date', 'Product-Name', 'Coin', 'Holding', 'S-Amount', 'Value Date', 'Status'],
  'Redemption': ['R-Redemption Date', 'Product-Name', 'Coin', 'Holding', 'Amount', 'Coupon', 'Total', 'R-Status'],
};

const KeyMap = {
  'Product-Name': {
    label: 'exchange-lending-productname',
    key: 'projectId',
    renderFunc: i18n => pid => getLabel(`exchange-lending-${pid}`, i18n),
  },
  'Coin': {
    label: 'exchange-common-coin',
    key: 'asset',
    width: '8%',
  },
  'Holding': {
    label: 'exchange-lending-holding',
    key: 'lot',
    width: '10%',
    align: 'right',
  },
  'Amount': {
    label: 'exchange-common-amount',
    key: 'principal',
    width: '10%',
    align: 'right',
  },
  'Duration': {
    label: 'exchange-lending-durations',
    key: 'duration',
    width: '10%',
    align: 'right',
  },
  'Value Date': {
    label: 'exchange-lending-vdate',
    key: 'startTime',
    width: '10%',
    renderFunc: () => time => moment(+time).format(TIME_FORMAT),
  },
  'Redemption Date': {
    label: 'exchange-lending-rdate',
    key: 'endTime',
    width: '12%',
    renderFunc: () => time => moment(+time).format(TIME_FORMAT),
  },
  'Coupon': {
    label: 'exchange-lending-coupon',
    key: 'interest',
    width: '10%',
    align: 'right',
  },
  'APR': {
    label: 'exchange-lending-APR',
    key: 'interestRate',
    align: 'right',
    width: '15%',
    renderFunc: () => num => pencentage(num, 1),
  },
  'Subscription Date': {
    label: 'exchange-lending-sdate',
    key: 'createTimestamp',
    width: '15%',
    renderFunc: () => time => moment(+time).format(TIME_HH_FORMAT),
  },
  'S-Amount': {
    label: 'exchange-common-amount',
    key: 'amount',
    width: '12%',
    align: 'right',
  },
  'Status': {
    label: 'exchange-common-status',
    key: 'status',
    width: '20%',
    renderFunc: i18n => key => getLabel(`exchange-lending-${key}`, i18n),
  },
  'R-Redemption Date': {
    label: 'exchange-lending-rdate',
    key: 'createTimestamp',
    width: '15%',
    renderFunc: () => time => moment(+time).format(TIME_HH_FORMAT),
  },
  'Total': {
    label: 'exchange-trade-Total',
    key: 'amount',
    align: 'right',
    width: '10%',
  },
  'R-Status': {
    label: 'exchange-common-status',
    key: 'status',
    renderFunc: i18n => () => getLabel('exchange-common-successful', i18n),
  },
};

type Props = {
  i18n: Object,
  type: string,
  locale: string,
  coinList: Array<Object>,
}

const HistoryTable = ({
  i18n, locale, coinList, type,
}: Props) => {
  const queryRef = useRef({});
  const fetchData = async (params: Object = {}) => {
    const rs = await fetchLendingDataSource(type, {
      ...params,
      ...queryRef.current,
    });
    return rs;
  };
  const {
    loading,
    dataSource,
    pagination,
    wrapperedFetchData,
    handlePaginationChange,
  } = usePagination(fetchData);
  const columns = useMemo(() => RowMap[type].map(item => {
    const {
      key, label, renderFunc, dataIndex = key, ...rest
    } = KeyMap[item];
    return {
      key,
      render: renderFunc && renderFunc(i18n),
      dataIndex,
      title: getLabel(label, i18n),
      ...rest,
    };
  }), [i18n]);

  useEffect(() => {
    wrapperedFetchData();
  }, []);

  return (
    <div>
      {
        type !== 'Assets' && (
          <Conditions
            i18n={i18n}
            locale={locale}
            coinList={coinList}
            historyType={type}
            queryRef={queryRef}
            fetchData={wrapperedFetchData}
          />
        )
      }
      <EmptyTable
        i18n={i18n}
        data={dataSource}
        pagination={pagination.total > 12 && pagination}
        loading={loading}
        columns={columns}
        onChange={handlePaginationChange}
        rowKey={({ createTimestamp }, index) => `${createTimestamp}${index}`}
      />
    </div>
  );
};

export default HistoryTable;
